import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Lock, Mail } from 'lucide-react';

export default function LoginPage() {
  const { loginWithGoogle, localLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Show a message if the user was bounced back here (e.g., unregistered Google account)
  useEffect(() => {
    if (location.state?.authError) {
      toast.error(location.state.authError, { duration: 7000 });
      navigate('/login', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      await localLogin(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(1200px circle at 10% 0%, rgba(255,255,255,0.15), transparent 55%), radial-gradient(900px circle at 90% 80%, rgba(255,255,255,0.1), transparent 50%)'
          }} />
        </div>
        <div className="relative z-10 text-primary-foreground text-center px-12">
          <div className="mb-8">
            <img src="/visaxpress-logo.png" alt="VisaXpress" className="h-28 mx-auto" style={{filter: 'brightness(0) invert(1)'}} />
          </div>
          <div className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-sm font-medium mb-6">
            FALCON
          </div>
          <p className="text-lg text-white/80 max-w-md mx-auto">
            Your centralized workspace for team management, operations, and compliance across all branches
          </p>
          <div className="mt-12 flex gap-8 justify-center text-sm text-white/60">
            <div>
              <p className="text-3xl font-heading font-bold text-white">2</p>
              <p>Offices</p>
            </div>
            <div>
              <p className="text-3xl font-heading font-bold text-white">8+</p>
              <p>Team Members</p>
            </div>
            <div>
              <p className="text-3xl font-heading font-bold text-white">24/7</p>
              <p>Access</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/visaxpress-logo.svg" alt="VisaXpress" className="h-10 object-contain" />
          </div>

          <Card className="shadow-lg border-0 lg:border" data-testid="login-card">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-heading">Welcome to Falcon</CardTitle>
              <CardDescription>Sign in to access your workspace</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs defaultValue="google" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="google" data-testid="login-tab-google">
                    <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Google SSO
                  </TabsTrigger>
                  <TabsTrigger value="password" data-testid="login-tab-password">
                    <Lock className="h-4 w-4 mr-2" />
                    Email & Password
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="google" className="space-y-4 mt-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Use your company Google account to sign in securely
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12 gap-3 text-base border-2 hover:bg-muted"
                    onClick={handleGoogleLogin}
                    data-testid="google-login-button"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Sign in with Google
                  </Button>
                </TabsContent>

                <TabsContent value="password" className="mt-4">
                  <form onSubmit={handleLocalLogin} className="space-y-4">
                    <p className="text-sm text-muted-foreground text-center">
                      For staff without a company Google account
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="your.email@visaxpress.ca"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="h-11 pl-10"
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="h-11 pl-10"
                          data-testid="login-password-input"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-11"
                      disabled={loading}
                      data-testid="login-submit-button"
                    >
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      Your password was set by your administrator. Contact them if you need to reset it.
                    </p>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            VisaXpress Falcon &copy; {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
}
